/*
 * VivaNutrition — dormant fallback for Shopify's legacy variant-selector helpers.
 *
 * STATUS — CORRECTED 2026-08-17. READ THIS BEFORE CHANGING ANYTHING.
 * This file was written on the belief that option_selection.js was retired and
 * returned 404, leaving the variant UI inert. That belief was WRONG. The library is
 * alive:
 *
 *     https://<shop>/cdn/shopifycloud/storefront/assets/themes_support/
 *         option_selection-b017cd28.js  ->  HTTP 200, 13026 bytes, 20 Shopify.* symbols
 *
 * The 404 came from probing a guessed CDN path rather than the fingerprinted URL
 * Liquid actually renders. Nothing was lost. All three APIs this file re-implements —
 * Shopify.OptionSelectors, Shopify.Image and Shopify.getProduct — are present in the
 * live platform libraries, so "the variant UI is inert because the library is gone"
 * was never true and no such breakage was ever observed interactively.
 *
 * WHY IT IS STILL HERE
 * head-script.liquid loads it BEFORE option_selection.js, which assigns
 * unconditionally, so the platform implementation overwrites everything below and this
 * file is inert at runtime. It remains only as a safety net should Shopify genuinely
 * retire these helpers later. Do not reorder the loads — putting this file last would
 * shadow a working platform library with a deliberately partial reimplementation.
 *
 * The dependency map below is still accurate and worth keeping: it is why a wholesale
 * clone of the library was rejected.
 *
 *   Shopify.optionsMap              theme-owned (ss_custom.js)
 *   Shopify.updateOptionsInSelector theme-owned (ss_custom.js)
 *   Shopify.linkOptionSelectors     theme-owned (ss_custom.js)
 *   Shopify.Products                theme-owned (libs.js / recently-viewed.js)
 *   Shopify.uniq / resizeImage      platform, with a local fallback in vn-shopify-cart-api.js
 *
 * Verify before trusting either reading:
 *     python scripts/shopify/verify_platform_assets.py --symbols
 *
 * See docs/activation/shopify-legacy-cdn-library-status-2026-08-17.md
 *
 * Every definition is typeof-guarded, so a platform or app implementation always wins.
 */
window.Shopify = window.Shopify || {};

(function (Shopify) {
  'use strict';

  function variantMatches(variant, chosen) {
    for (var i = 0; i < chosen.length; i++) {
      if (chosen[i] === null || chosen[i] === undefined) continue;
      if (variant.options[i] !== chosen[i]) return false;
    }
    return true;
  }

  function readVariantFromUrl() {
    var match = window.location.search.match(/[?&]variant=(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  /* --------------------------------------------------- Shopify.OptionSelectors */

  if (typeof Shopify.OptionSelectors !== 'function') {
    /*
     * @param {string} masterSelectId  id of the hidden <select name="id"> holding variants
     * @param {object} options         { product, onVariantSelected, enableHistoryState }
     */
    Shopify.OptionSelectors = function (masterSelectId, options) {
      options = options || {};

      var master = document.getElementById(masterSelectId);
      var product = options.product;
      if (!master || !product || !product.variants) return;

      var self = this;

      /*
       * The theme's swatch code does jQuery('#' + selector.domIdPrefix).parents('form'),
       * so domIdPrefix must be a real element id. The retired library used the master
       * select id, and the theme's markup relies on that, so keep it identical.
       */
      this.domIdPrefix = masterSelectId;
      this.product = product;
      this.selectors = [];

      var container = document.createElement('div');
      container.className = 'vn-option-selectors';

      (product.options || []).forEach(function (optionName, index) {
        var values = [];
        product.variants.forEach(function (variant) {
          var value = variant.options[index];
          if (values.indexOf(value) === -1) values.push(value);
        });

        var wrapper = document.createElement('div');
        wrapper.className = 'selector-wrapper';

        var label = document.createElement('label');
        label.setAttribute('for', masterSelectId + '-option-' + index);
        label.textContent = typeof optionName === 'string' ? optionName : (optionName.name || '');

        var select = document.createElement('select');
        select.className = 'single-option-selector single-option-selector-' + masterSelectId;
        select.id = masterSelectId + '-option-' + index;
        select.setAttribute('data-option-index', index);

        values.forEach(function (value) {
          var opt = document.createElement('option');
          opt.value = value;
          opt.textContent = value;
          select.appendChild(opt);
        });

        select.addEventListener('change', function () {
          self.selectVariantFromSelectors();
        });

        wrapper.appendChild(label);
        wrapper.appendChild(select);
        container.appendChild(wrapper);
        self.selectors.push(select);
      });

      if (master.parentNode) {
        master.parentNode.insertBefore(container, master.nextSibling);
      }

      this.currentChoices = function () {
        return self.selectors.map(function (s) { return s.value; });
      };

      this.findVariant = function (chosen) {
        for (var i = 0; i < product.variants.length; i++) {
          if (variantMatches(product.variants[i], chosen)) return product.variants[i];
        }
        return null;
      };

      this.selectVariantFromSelectors = function () {
        var variant = self.findVariant(self.currentChoices());
        self.applyVariant(variant);
      };

      this.applyVariant = function (variant) {
        /*
         * Keeping the master <select name="id"> authoritative is what makes add-to-cart
         * post the correct variant. The theme reads name=id from the form.
         */
        if (variant) {
          master.value = variant.id;
          if (options.enableHistoryState && window.history && window.history.replaceState) {
            var url = window.location.pathname + '?variant=' + variant.id;
            window.history.replaceState({ variantId: variant.id }, '', url);
          }
        }
        if (typeof options.onVariantSelected === 'function') {
          options.onVariantSelected.call(self, variant, self);
        }
      };

      /* Initial selection: deep-linked variant, else the master select's value, else first available. */
      var initialId = readVariantFromUrl() || parseInt(master.value, 10) || null;
      var initial = null;

      if (initialId) {
        product.variants.forEach(function (v) { if (v.id === initialId) initial = v; });
      }
      if (!initial) {
        for (var i = 0; i < product.variants.length; i++) {
          if (product.variants[i].available) { initial = product.variants[i]; break; }
        }
      }
      if (!initial) initial = product.variants[0];

      if (initial) {
        this.selectors.forEach(function (s, index) { s.value = initial.options[index]; });
        this.applyVariant(initial);
      }
    };
  }

  /* --------------------------------------------------------- Shopify.Image */

  Shopify.Image = Shopify.Image || {};

  if (typeof Shopify.Image.switchImage !== 'function') {
    /*
     * Swaps a product image to the variant's featured image, preserving the requested
     * size suffix already present on the element so layout and CLS are unchanged.
     */
    Shopify.Image.switchImage = function (newImage, element, callback) {
      if (!newImage || !element) return;

      var src = newImage.src || newImage;
      var current = element.src || '';
      var sizeMatch = current.match(/_(\d+x\d*|\d*x\d+|x\d+)\./);
      var sized = src;

      if (sizeMatch && typeof Shopify.resizeImage === 'function') {
        sized = Shopify.resizeImage(src, sizeMatch[1]);
      }

      element.src = sized;
      if (newImage.alt) element.alt = newImage.alt;

      if (typeof callback === 'function') callback(sized, newImage, element);
    };
  }

  /* ------------------------------------------------------- Shopify.getProduct */

  if (typeof Shopify.getProduct !== 'function') {
    Shopify.getProduct = function (handle, callback) {
      var url = '/products/' + handle + '.js';

      function done(data) { if (typeof callback === 'function') callback(data); }
      function fail(err) {
        if (window.console && console.warn) console.warn('[VN] Shopify.getProduct failed:', err);
      }

      if (typeof window.fetch === 'function') {
        window
          .fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
          .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
          .then(done)
          .catch(fail);
        return;
      }

      var xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { done(JSON.parse(xhr.responseText)); } catch (e) { fail(e); }
        } else { fail(new Error('HTTP ' + xhr.status)); }
      };
      xhr.onerror = function () { fail(new Error('network error')); };
      xhr.send(null);
    };
  }
})(window.Shopify);
